import { useEffect, useState, useCallback } from "react";

function getTimeOfDay() {
  const h = new Date().getHours();
  if (h >= 6 && h < 12) return "morning";
  if (h >= 12 && h < 18) return "afternoon";
  return "night";
}

function isAfterMidnight() {
  const h = new Date().getHours();
  return h >= 0 && h < 6;
}

function applyTimeTheme() {
  const b = document.body;
  b.classList.remove("theme-morning", "theme-afternoon", "theme-night", "cursor-dollar");
  const tod = getTimeOfDay();
  b.classList.add(`theme-${tod}`);
  if (isAfterMidnight()) b.classList.add("cursor-dollar");
}

function removeBadge() {
  const badge = document.getElementById("emergent-badge");
  if (badge) badge.remove();
}

function getWeatherType(code, temp, countryCode) {
  const rainCodes = [51, 53, 55, 61, 63, 65, 80, 81, 82, 95, 96, 99];
  const snowCodes = [71, 73, 75, 77, 85, 86];
  const cloudyCodes = [2, 3, 45, 48];

  if (rainCodes.includes(code)) return "rain";
  if (snowCodes.includes(code) || temp < 0) return "snow";
  if (temp > 38 || countryCode === "AE") return "hot";
  if (cloudyCodes.includes(code)) return "cloudy";
  return "clear";
}

function applyWeatherClass(type) {
  const b = document.body;
  b.classList.remove("weather-rain", "weather-snow", "weather-hot", "weather-cloudy");
  if (type !== "clear") b.classList.add(`weather-${type}`);
}

function generateParticles(timeOfDay, weatherType) {
  const items = [];
  if (timeOfDay === "night") {
    for (let i = 0; i < 40; i++) {
      items.push({ id: `star-${i}`, type: "star", left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, size: Math.random() * 2 + 1, delay: Math.random() * 5, duration: Math.random() * 3 + 2 });
    }
  }
  if (weatherType === "rain") {
    for (let i = 0; i < 30; i++) {
      items.push({ id: `rain-${i}`, type: "rain", left: `${Math.random() * 100}%`, delay: Math.random() * 2, duration: Math.random() * 1 + 0.8 });
    }
  } else if (weatherType === "snow") {
    for (let i = 0; i < 25; i++) {
      items.push({ id: `snow-${i}`, type: "snow", left: `${Math.random() * 100}%`, size: Math.random() * 3 + 2, delay: Math.random() * 5, duration: Math.random() * 5 + 5 });
    }
  }
  return items;
}

export default function ThemeEngine() {
  const [particles, setParticles] = useState([]);
  const [weather, setWeather] = useState("clear");

  const detectWeather = useCallback(async () => {
    try {
      const geo = await fetch("https://ipwho.is/").then(r => r.json());
      if (!geo.success) return;
      const w = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${geo.latitude}&longitude=${geo.longitude}&current_weather=true`).then(r => r.json());
      const code = w.current_weather?.weathercode ?? 0;
      const temp = w.current_weather?.temperature ?? 25;
      const type = getWeatherType(code, temp, geo.country_code);
      applyWeatherClass(type);
      setWeather(type);
    } catch (err) {
      console.warn("Weather detection failed:", err.message);
    }
  }, []);

  useEffect(() => {
    removeBadge();
    const badgeObserver = new MutationObserver(removeBadge);
    badgeObserver.observe(document.body, { childList: true, subtree: true });

    applyTimeTheme();
    const timer = setInterval(applyTimeTheme, 60000);
    const weatherTimer = setTimeout(detectWeather, 2000);

    return () => { clearInterval(timer); clearTimeout(weatherTimer); badgeObserver.disconnect(); };
  }, [detectWeather]);

  useEffect(() => {
    const tod = getTimeOfDay();
    setParticles(generateParticles(tod, weather));
  }, [weather]);

  if (particles.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[1] pointer-events-none overflow-hidden" aria-hidden="true">
      {particles.map(p => {
        if (p.type === "star") return (
          <div key={p.id} className="absolute rounded-full bg-white theme-star"
            style={{ left: p.left, top: p.top, width: p.size, height: p.size, animationDelay: `${p.delay}s`, animationDuration: `${p.duration}s` }} />
        );
        if (p.type === "rain") return (
          <div key={p.id} className="absolute theme-rain"
            style={{ left: p.left, animationDelay: `${p.delay}s`, animationDuration: `${p.duration}s` }} />
        );
        if (p.type === "snow") return (
          <div key={p.id} className="absolute rounded-full bg-white/60 theme-snow"
            style={{ left: p.left, width: p.size, height: p.size, animationDelay: `${p.delay}s`, animationDuration: `${p.duration}s` }} />
        );
        return null;
      })}
    </div>
  );
}
